import styled from "@emotion/styled";
import {
  Box,
  Button,
  Switch,
  FormControl,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import BusinessIcon from "@mui/icons-material/Business";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Divider from "@mui/material/Divider";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { ROUTE_PATHS } from "../../../routes/routePath";
import {
  getWithQueryApiServices,
  putWithQueryApiServices,
  postWithQueryApiServices,
  deletewithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { userFormInitVal } from "../../../constants";
import { SplitContainer, StyledHeader } from "../../../styles/commonStyles";
import { successToast, errorToast, infoToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { AccessList } from "./AccessList";
import { DataGrid, GridEditInputCell } from "@mui/x-data-grid";

import { useTenantGuard } from "../../../hooks/useTenantGuard";
import { usePermissions } from "../../../hooks/usePermissions";
import { LoadingScreen } from "../../shared/LoadingScreen";
import { PermissionDenied } from "../../shared/PermissionDenied";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "10px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1%",
  },
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
}));

const CompanyContainer = styled(Box)(({ theme, hasuser }) => ({
  minHeight: hasuser === "true" ? "700px" : "150px",
  height: "auto",
  width: "100%",
  marginTop: 20,
  [theme.breakpoints.down("md")]: {
    marginTop: 28,
  },
  backgroundColor: "#f1f1f1",
  borderRadius: 14,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  transition: "min-height 0.3s ease",
}));

const DisabledOverlay = styled(Box)(({ disabled }) => ({
  position: "relative",
  transition: "all 0.3s ease",
  pointerEvents: "auto",
  "&::after": disabled
    ? {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.01)",
        pointerEvents: "none",
        zIndex: 1,
        borderRadius: 8,
      }
    : {},
}));

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const StyledTabs = styled(Tabs)(({ disabled }) => ({
  backgroundColor: "#fff",
  borderRadius: "8px 8px 0 0",
  opacity: disabled ? 0.6 : 1,
  pointerEvents: disabled ? "none" : "auto",
  cursor: disabled ? "not-allowed" : "auto",
  "& .MuiTabs-indicator": {
    backgroundColor: "#1976d2",
  },
}));

const StyledTab = styled(Tab)(({ disabled }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  cursor: disabled ? "not-allowed" : "pointer",
  "&.Mui-selected": {
    color: "#1976d2",
  },
}));

// ✅ #8: Security Badge for dialog
const SecurityBadge = styled(Chip)(({ securitytype }) => ({
  fontSize: 10,
  height: 22,
  backgroundColor:
    securitytype === "anywhere"
      ? "#e8f5e9"
      : securitytype === "user"
        ? "#e3f2fd"
        : securitytype === "company"
          ? "#fff3e0"
          : "#f5f5f5",
  color:
    securitytype === "anywhere"
      ? "#2e7d32"
      : securitytype === "user"
        ? "#1565c0"
        : securitytype === "company"
          ? "#f57c00"
          : "#666",
  fontWeight: 500,
}));

const GridSelectCell = ({ params, value, onChange, options, disabled, displayEmpty, className, px = 0, children }) => {
  const [val, setVal] = useState(value);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    if (params.hasFocus && containerRef.current) {
      const timer = setTimeout(() => {
        const selectButton = containerRef.current?.querySelector('[role="combobox"]');
        if (selectButton) {
          selectButton.focus();
        } else if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [params.hasFocus]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e) => {
      if (e.key === "Tab") {
        e.stopPropagation();
      } else if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.stopPropagation();
        e.preventDefault();
        const char = e.key.toLowerCase();
        const match = options.find((opt) =>
          opt.label.toLowerCase().split(/\s+/).some((word) => word.startsWith(char))
        );
        if (match !== undefined) {
          setVal(match.value);
          onChange(match.value);
        }
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Enter" ||
        e.key === "Escape"
      ) {
        e.stopPropagation();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [options, params, onChange]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
        height: "100%",
        px: px,
        flex: 1,
        minWidth: 0,
      }}
    >
      <FormControl fullWidth size="small" sx={{ minWidth: 0, maxWidth: { xs: 120, md: "100%" } }}>
        <Select
          inputRef={inputRef}
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            onChange(e.target.value);
          }}
          disabled={disabled}
          displayEmpty={displayEmpty}
          className={className}
        >
          {children}
        </Select>
      </FormControl>
    </Box>
  );
};

const UserSecurityEditCell = ({ params, securityProfilesData }) => {
  const initialValue = params.value !== undefined && params.value !== null
    ? params.value
    : params.row.allowAnywhere
      ? "anywhere"
      : params.row.securityprofileID
        ? Number(params.row.securityprofileID)
        : "";

  const options = useMemo(() => [
    { label: "From Anywhere", value: "anywhere" },
    { label: "None", value: "" },
    ...(securityProfilesData?.data?.list?.map((p) => ({
      label: p.name,
      value: Number(p.securityprofileID),
    })) || []),
  ], [securityProfilesData]);

  return (
    <GridSelectCell
      params={params}
      value={initialValue}
      onChange={(val) => {
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: val,
        });
      }}
      options={options}
      displayEmpty
      className="security-settings-select"
      px={1}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </GridSelectCell>
  );
};

const CopyPermissionsSelect = ({
  params,
  copyUsersList,
  copyPermissionUserId,
  handleCopyPermissions,
  isTenantBlocked,
}) => {
  const containerRef = useRef(null);

  const uniqueUsersMap = new Map();
  (copyUsersList?.data?.userList || []).forEach((u) => {
    if (u.userID && u.userID !== params.row.userID) {
      uniqueUsersMap.set(u.userID, u);
    }
  });
  const companyUsers = Array.from(uniqueUsersMap.values());

  const options = useMemo(() => [
    { label: "Copy from...", value: "" },
    ...companyUsers.map((u) => ({
      label: u.username,
      value: u.userID,
    })),
  ], [companyUsers]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e) => {
      if (e.key === "Tab") {
        e.stopPropagation();
      } else if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.stopPropagation();
        e.preventDefault();
        const char = e.key.toLowerCase();
        const match = options.find((opt) =>
          opt.label.toLowerCase().split(/\s+/).some((word) => word.startsWith(char))
        );
        if (match !== undefined && match.value !== "") {
          handleCopyPermissions(match.value, params);
        }
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Enter" ||
        e.key === "Escape"
      ) {
        e.stopPropagation();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [options, params, handleCopyPermissions]);

  // Restore focus to the Select button after permissions are copied to prevent focus loss
  useEffect(() => {
    if (copyPermissionUserId && containerRef.current) {
      const timer = setTimeout(() => {
        const selectButton =
          containerRef.current?.querySelector('[role="combobox"]');
        if (selectButton) {
          selectButton.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [copyPermissionUserId]);

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", alignItems: "center", gap: 1, mr: 0.5 }}
    >
      <FormControl
        size="small"
        sx={{ width: 180 }}
        data-copy-permissions="true"
      >
        <Select
          value={copyPermissionUserId}
          displayEmpty
          onChange={(e) => handleCopyPermissions(e.target.value, params)}
          disabled={isTenantBlocked}
          sx={{ height: 32, fontSize: "12px" }}
        >
          <MenuItem value="" disabled>
            <em>Copy from...</em>
          </MenuItem>
          {companyUsers.map((user) => (
            <MenuItem key={user.userID} value={user.userID}>
              {user.username}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Divider
        orientation="vertical"
        flexItem
        sx={{ height: 20, my: "auto" }}
      />
    </Box>
  );
};

export const User = () => {
  const cookies = new Cookies();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const defaultcompany = cookies.get("defaultcompany");
  const [selectedUser, setSelectedUser] = useState("");
  const [addNewUser, setAddnewUser] = useState(false);
  const outletContext = useOutletContext();
  const {
    tenantString,
    currentTenant,
    currentCompany,
    setCurrentCompany,
    fullCompanyList,
    isCompanySwitching,
  } = outletContext;
  const [searchUser, setSearchUser] = useState("");
  const [debouncedSearchUser, setDebouncedSearchUser] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchUser(searchUser);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchUser]);

  const [userDetails, setUserDetails] = useState(userFormInitVal);
  const [outerValue, setOuterValue] = useState(null);
  const [pendingSelectUserEmail, setPendingSelectUserEmail] = useState(null);
  const [copyPermissionUserId, setCopyPermissionUserId] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [isEditingGrid, setIsEditingGrid] = useState(false);
  const [isEditingAccessList, setIsEditingAccessList] = useState(false);
  const [hasPermissionsChanged, setHasPermissionsChanged] = useState(false);
  const [permissionCompany, setPermissionCompany] = useState(currentCompany);
  const [copySessionSource, setCopySessionSource] = useState(null);
  const savedAccessListRef = useRef(null);
  const hasUnsavedCopyRef = useRef(false);

  const cloneAccessList = (list) => JSON.parse(JSON.stringify(list || []));

  const commitSavedAccessList = useCallback((list) => {
    savedAccessListRef.current = cloneAccessList(list);
    hasUnsavedCopyRef.current = false;
  }, []);

  const changePermissionCompany = (nextId) => {
    if (nextId == null || Number(nextId) === Number(permissionCompany)) return;
    if (hasUnsavedCopyRef.current && savedAccessListRef.current) {
      setUserDetails((prev) => ({
        ...prev,
        accessList: cloneAccessList(savedAccessListRef.current),
      }));
    }
    hasUnsavedCopyRef.current = false;
    setCopySessionSource(null);
    setPermissionCompany(nextId);
  };

  useEffect(() => {
    if (!currentCompany) return;
    changePermissionCompany(currentCompany);
  }, [currentCompany]);

  // ✅ Security info dialog state
  const [securityInfoDialog, setSecurityInfoDialog] = useState({
    open: false,
    user: null,
  });

  // ✅ Admin Role Transfer dialog state
  const [transferAdminDialog, setTransferAdminDialog] = useState({
    open: false,
    targetUser: null,
    targetParams: null,
    isRequest: false,
  });

  // ✅ Store fetched user details for security dialog
  const [securityUserDetails, setSecurityUserDetails] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);

  const { isTenantBlocked, tenantMessage } = useTenantGuard();
  const {
    hasPermission,
    isGlobalSuperAdmin,
    isTenantAdmin,
    isSystemAdmin,
    resolvePermission,
  } = usePermissions();

  // ✅ Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    loading: false,
  });

  const isEditing = isEditingGrid || isEditingAccessList;

  // Reset copyPermissionUserId when editing is stopped
  useEffect(() => {
    if (!isEditingGrid) {
      setCopyPermissionUserId("");
    }
  }, [isEditingGrid]);

  // ✅ REFINED ROLE DETECTION - Using centralized hook values
  const rawTenantAdminCookie = cookies.get("isTenantAdmin");
  const rawSystemAdminCookie = cookies.get("isSystemAdmin");

  // Helper to normalize truthy values from cookies (can be boolean, string "true", or string "1")
  const isTruthy = (val) =>
    val === true ||
    val === "true" ||
    val === 1 ||
    val === "1" ||
    val === "TRUE" ||
    val === "yes";

  const isActualTenantAdmin = isTruthy(rawTenantAdminCookie);
  const isActualSystemAdmin = isTruthy(rawSystemAdminCookie);

  // Active users query — always cross-company. Backend returns every user in
  // companies the requester can access (all companies for tenant/global admins).
  const {
    data: allUsersList,
    isLoading: isUsersLoading,
    refetch: userListRefetch,
    isFetching: usersFetching,
  } = useQuery({
    queryKey: [
      "usersList",
      debouncedSearchUser,
      tenantString,
      paginationModel,
      currentCompany,
    ],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_USERS_LIST,
        `pageNumber=${paginationModel.page + 1}&pageSize=${paginationModel.pageSize}&search=${debouncedSearchUser}&selectedCompany=${currentCompany || ""}${tenantString}`,
      ),
    enabled: !!currentCompany,
    placeholderData: keepPreviousData,
  });

  // Query to fetch all users (non-paginated) for copying permissions dropdown
  const { data: copyUsersList } = useQuery({
    queryKey: ["copyUsersList", tenantString, currentCompany],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_USERS_LIST,
        `pageNumber=1&pageSize=10000&selectedCompany=${currentCompany || ""}${tenantString}`,
      ),
    enabled: !!currentCompany,
  });

  useEffect(() => {
    if (pendingSelectUserEmail && allUsersList?.data?.userList) {
      const newUser = allUsersList.data.userList.find(
        (u) => u.title === pendingSelectUserEmail,
      );
      if (newUser) {
        setSelectedUser(newUser.userID);
        setAddnewUser(true);
        setPendingSelectUserEmail(null);
      }
    }
  }, [allUsersList, pendingSelectUserEmail]);

  // Reset pagination to first page when search text changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchUser]);

  // Extract current user's role from the list
  const currentUserId = cookies.get("userId");
  const currentUser = copyUsersList?.data?.userList?.find(
    (u) => Number(u.userID) === Number(currentUserId),
  ) || allUsersList?.data?.userList?.find(
    (u) => Number(u.userID) === Number(currentUserId),
  );

  // ✅ IMPORTANT: Identify if current user is THE admin for this company
  const isCurrentUserSysAdmin =
    isGlobalSuperAdmin ||
    isActualTenantAdmin ||
    isActualSystemAdmin ||
    isSystemAdmin ||
    currentUser?.issysadm === 1 ||
    isTruthy(currentUser?.isTenantAdmin);

  const isUserInCompany =
    isGlobalSuperAdmin || isActualTenantAdmin || !!currentUser;

  // Permission flags: Require Company Admin, Tenant Admin, or Global Super Admin
  const canCreateUser = isGlobalSuperAdmin || isActualTenantAdmin || hasPermission("Edit Users");
  const canUpdateUser = isGlobalSuperAdmin || isActualTenantAdmin || hasPermission("Edit Users");
  const canDeleteUser = isGlobalSuperAdmin || isActualTenantAdmin || hasPermission("Edit Users");
  const canReadUser = isGlobalSuperAdmin || hasPermission("Users: Read");

  const handleGlobalCompanyChange = (e) => {
    // This will be handled by the context/navbar usually, but we need it local if missing
    const { setCurrentCompany } = outletContext; // Need to check if this is available
    if (setCurrentCompany) setCurrentCompany(e.target.value);
  };

  // Security profiles query - Always enabled to ensure dropdowns populate
  const { data: securityProfilesData, isLoading: isProfilesLoading } = useQuery(
    {
      queryKey: ["securityProfiles", tenantString],
      queryFn: () =>
        getWithQueryApiServices(
          apiRoutes.GET_SECURITY_PROFILE_LIST,
          `pageNumber=1${tenantString}`,
        ),
    },
  );

  // ✅ New Query for Role Change Requests
  const { data: roleRequests, refetch: roleRequestsRefetch } = useQuery({
    queryKey: ["roleRequests", currentCompany, tenantString],
    enabled: !!currentCompany && isCurrentUserSysAdmin,
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ROLE_CHANGE_REQUESTS,
        `selectedCompany=${currentCompany}${tenantString}`,
      ),
  });

  // ✅ New Mutation for Handling Requests
  const handleRoleRequest = useMutation({
    mutationKey: ["handleRoleRequest"],
    mutationFn: ({ requestId, action }) =>
      postWithQueryApiServices(
        `${apiRoutes.HANDLE_ROLE_CHANGE_REQUEST}/${requestId}/handle`,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        { action },
      ),
    onSuccess: (data) => {
      successToast(data.data.message || "Request handled successfully");
      roleRequestsRefetch();
      userListRefetch();

      // If approved, current user was demoted and should be logged out
      if (data.data.logout) {
        // Clear all cookies (except tenantName)
        const allCookies = cookies.getAll();
        Object.keys(allCookies).forEach((cookieName) => {
          if (cookieName !== "tenantName") {
            cookies.remove(cookieName, { path: "/" });
          }
        });

        // Redirect to login
        setTimeout(() => {
          navigate(ROUTE_PATHS.LOGIN || "/");
        }, 1500);
      }
    },
    onError: (error) => {
      errorToast(error.response?.data?.message || "Failed to process request");
    },
  });

  // ✅ UPDATED: Get security profile name with better error handling
  const getSecurityProfileName = (profileId) => {
    if (!securityProfilesData?.data?.list || !profileId) return "No Profile";

    const profile = securityProfilesData.data.list.find(
      (p) =>
        p.securityprofileID === profileId ||
        p.securityprofileID === parseInt(profileId),
    );

    return profile?.name || `Profile ${profileId}`;
  };

  const getCompanySecurityInfo = (companyId) => {
    if (!fullCompanyList) return null;

    const company = fullCompanyList.find(
      (c) => c.companyID === companyId || c.companyID === parseInt(companyId),
    );

    if (!company) {
      console.warn(`Company with ID ${companyId} not found`);
      return null;
    }

    return {
      name: company.name || `Company ${companyId}`,
      companyID: company.companyID,
      securityprofileID: company.securityprofileID,
      profileName: company.securityprofileID
        ? getSecurityProfileName(company.securityprofileID)
        : null,
    };
  };

  // ✅ UPDATED: Get user's companies security info using defaultCompany and available company data
  const getUserCompaniesSecurityInfo = () => {
    if (!securityUserDetails) return [];

    // Get companies from user's access or defaultCompany
    const userCompanies = [];

    // Add default company if exists
    if (
      securityUserDetails.defaultCompany &&
      securityUserDetails.defaultCompany !== "undefined"
    ) {
      const companyId = parseInt(securityUserDetails.defaultCompany);
      const companyInfo = getCompanySecurityInfo(companyId);
      if (companyInfo) {
        userCompanies.push(companyInfo);
      }
    }

    // If there's an accessList, add those companies too
    if (
      securityUserDetails.accessList &&
      Array.isArray(securityUserDetails.accessList)
    ) {
      securityUserDetails.accessList.forEach((access) => {
        if (
          access.companyID &&
          access.companyID !== securityUserDetails.defaultCompany
        ) {
          const companyInfo = getCompanySecurityInfo(access.companyID);
          if (companyInfo) {
            userCompanies.push(companyInfo);
          }
        }
      });
    }

    return userCompanies;
  };

  // ✅ #8: Handle showing security info dialog - FETCHES user's data
  const handleShowSecurityInfo = async (user) => {
    setSecurityLoading(true);
    setSecurityInfoDialog({
      open: true,
      user: user,
    });

    try {
      const { data } = await getWithQueryApiServices(
        `${apiRoutes.GET_USER_DETAILS_BY_ID}/${user.userID}`,
        `selectedCompany=${defaultcompany}${tenantString}`,
      );
      setSecurityUserDetails(data);
    } catch (error) {
      console.error("Failed to load user security details", error);
      setSecurityUserDetails(null);
    } finally {
      setSecurityLoading(false);
    }
  };

  // ✅ #8: Close security dialog and reset
  const handleCloseSecurityInfoDialog = () => {
    setSecurityInfoDialog({ open: false, user: null });
    setSecurityUserDetails(null);
    setSecurityLoading(false);
  };

  const handleSecuritySettingChange = (user, newValue) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (user.isNew) {
      setOuterValue({
        allowAnywhere: newValue === "anywhere" ? 1 : 0,
        securityprofileID:
          newValue === "anywhere" || newValue === "" ? null : newValue,
        isNew: true,
        id: user.id,
      });
    } else {
      const updatedUser = {
        ...user,
        allowAnywhere: newValue === "anywhere" ? 1 : 0,
        securityprofileID:
          newValue === "anywhere" || newValue === "" ? null : newValue,
      };

      if (!user.isNew) {
        updateUserDetails.mutate(updatedUser);
      }
    }
  };

  const handleAdminIconClick = (params) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    const { row } = params;

    // Visibility Check logic
    if (!isCurrentUserSysAdmin) {
      if (row.issysadm || row.isTenantAdmin) {
        successToast("This user is an Administrator.");
      } else {
        infoToast(
          "Only a System Admin can assign this role. Please contact your administrator.",
        );
      }
      return;
    }

    // Role Management for Tenant Admin (The Boss)
    if (currentUser?.isTenantAdmin) {
      // Tenant admin can move the shield to anyone or take it back
      if (row.userID === parseInt(currentUserId)) {
        infoToast(
          "You are the Tenant Admin. This purple status is permanent. If you want to hold the Company Shield, you can move it to yourself from another admin.",
        );
        return;
      }

      // If clicking on a row that currently has the Company Shield (issysadm)
      if (row.issysadm) {
        // Option: Remove the shield (Revoke Admin) or suggest handover
        handleSwitchChange(params, "issysadm", { target: { checked: false } });
        return;
      }

      // If clicking on non-admin, trigger assignment
      const hasExistingAdmin = allUsersList?.data?.userList?.some(
        (u) => u.issysadm === 1 && !u.isTenantAdmin,
      );
      setTransferAdminDialog({
        open: true,
        targetUser: row,
        targetParams: params,
        isRequest: hasExistingAdmin,
      });
      return;
    }

    // Role Management for regular Company Admins (Shield holders)
    if (row.userID === parseInt(currentUserId)) {
      if (row.issysadm) {
        infoToast(
          "You are the Company Admin. To transfer this role, click the shield icon on another user.",
        );
      }
      return;
    }

    // Prevent regular Company Admins from touching the Tenant Admin
    if (row.isTenantAdmin) {
      errorToast("You cannot manage the Tenant Admin's role.");
      return;
    }

    if (row.issysadm) {
      handleSwitchChange(params, "issysadm", { target: { checked: false } });
      return;
    }

    setTransferAdminDialog({
      open: true,
      targetUser: row,
      targetParams: params,
      isRequest: false,
    });
  };

  const confirmTransferAdmin = () => {
    const { targetParams } = transferAdminDialog;
    setTransferAdminDialog({
      open: false,
      targetUser: null,
      targetParams: null,
    });

    // Trigger the switch change (logic handled in backend for handover)
    handleSwitchChange(targetParams, "issysadm", { target: { checked: true } });
  };

  const handleSwitchChange = (params, field, e) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (params.row.isNew) {
      setOuterValue({
        [field]: e.target.checked ? 1 : 0,
        isNew: true,
        id: params.row.id,
      });
    } else {
      updateUserDetails.mutate({
        ...params.row,
        [field]: e.target.checked ? 1 : 0,
        isRoleChange: field === "issysadm",
      });
    }
  };

  const handleGridEditingStateChange = useCallback((editing) => {
    setIsEditingGrid(editing);
  }, []);

  const handleAccessListEditingStateChange = useCallback((editing) => {
    setIsEditingAccessList(editing);
  }, []);

  // ✅ ORIGINAL COLUMNS - UNCHANGED + Small security icon added
  const columns = [
    {
      field: "username",
      headerName: "User Name",
      flex: 1,
      editable: !isTenantBlocked,
      required: true,
      renderCell: (params) => {
        const hasPendingRequest = roleRequests?.data?.some(
          (req) => Number(req.targetUserID) === Number(params.row.userID),
        );
        return (
          <Box
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                handleUserSelection(params.row);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: "100%",
              outline: "none",
              cursor: "pointer",
              "&:focus-visible": {
                outline: "2px solid #1976d2",
                outlineOffset: "2px",
                borderRadius: "4px",
              },
            }}
          >
            <Typography variant="body2">{params.row.username}</Typography>
            {isCurrentUserSysAdmin && Number(params.row.issysadm) === 1 && (
              <Tooltip title="Company Admin for the company selected in the header. Regular users do not see this.">
                <Chip
                  label="Company Admin"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: "#e3f2fd",
                    color: "#1565c0",
                  }}
                />
              </Tooltip>
            )}
            {hasPendingRequest && (
              <Tooltip title="A promotion to Company Admin is pending approval from the current admin.">
                <Chip
                  label="Promotion Pending"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            autoComplete: "one-time-code",
          }}
        />
      ),
    },
    {
      field: "title",
      headerName: "Email",
      editable: !isTenantBlocked,
      required: true,
      flex: 1,
      pattern: /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/,
      patternError: "Please enter a valid email address.",
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            autoComplete: "one-time-code",
          }}
        />
      ),
    },
    {
      field: "securityprofileID",
      headerName: "Security Settings",
      flex: 1,
      required: false,
      editable: canUpdateUser || isGlobalSuperAdmin,
      renderCell: (params) => {
        const securityProfileValue = params.row.allowAnywhere
          ? "anywhere"
          : params.row.securityprofileID
            ? Number(params.row.securityprofileID)
            : "";

        const options = [
          { label: "From Anywhere", value: "anywhere" },
          { label: "None", value: "" },
          ...(securityProfilesData?.data?.list?.map((p) => ({
            label: p.name,
            value: Number(p.securityprofileID),
          })) || []),
        ];

        return (
          <Box
            id="tour-user-security-settings"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              width: "100%",
              height: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <Tooltip
              title={
                isTenantBlocked
                  ? tenantMessage
                  : isEditingAccessList
                    ? "Complete access list changes first"
                    : ""
              }
            >
              <FormControl fullWidth size="small" sx={{ minWidth: 0, maxWidth: { xs: 120, md: "100%" } }}>
                <Select
                  value={securityProfileValue}
                  className="security-settings-select"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleSecuritySettingChange(params.row, val);
                    params.api.updateRows([
                      {
                        id: params.id,
                        allowAnywhere: val === "anywhere" ? 1 : 0,
                        securityprofileID:
                          val === "anywhere" || val === "" ? null : Number(val),
                      },
                    ]);
                  }}
                  displayEmpty
                  disabled={
                    isTenantBlocked ||
                    isEditingAccessList ||
                    !isCurrentUserSysAdmin ||
                    isEditingGrid
                  }
                >
                  <MenuItem value="anywhere">From Anywhere</MenuItem>
                  <MenuItem value="">None</MenuItem>
                  {securityProfilesData?.data?.list?.map((profile) => (
                    <MenuItem
                      key={profile.securityprofileID}
                      value={Number(profile.securityprofileID)}
                    >
                      {profile.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>

            {/* ✅ #8: Small security info icon */}
            {!params.row.isNew && (
              <Tooltip
                title={
                  params.row.allowAnywhere
                    ? "Green shield: this user can sign in from anywhere."
                    : params.row.securityprofileID
                      ? "Blue shield: this user must match a security profile."
                      : "Grey shield: no security profile is assigned."
                }
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowSecurityInfo(params.row);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <SecurityIcon
                    sx={{
                      fontSize: 16,
                      color: params.row.allowAnywhere
                        ? "#4caf50"
                        : params.row.securityprofileID
                          ? "#1976d2"
                          : "#999",
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
      renderEditCell: (params) => (
        <UserSecurityEditCell
          params={params}
          securityProfilesData={securityProfilesData}
        />
      ),
    },
  ];

  // Helper render for custom inline actions in the Actions column
  const renderExtraActions = (params, helpers) => {
    // Visibility Check: Only show if user has at least one management permission
    if (!canUpdateUser && !canDeleteUser && !isGlobalSuperAdmin) return null;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          height: "100%",
        }}
      >
        {/* Reset Password */}
        {!params.row.isNew && canUpdateUser && (
          <>
            <Tooltip
              title={
                isTenantBlocked
                  ? tenantMessage
                  : isEditing
                    ? "Complete current operation first"
                    : "Reset Password"
              }
            >
              <span>
                <Button
                  id="tour-user-reset-btn"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isTenantBlocked) {
                      errorToast(tenantMessage);
                      return;
                    }
                    if (isEditing) {
                      errorToast(
                        "Please complete or cancel the current operation first.",
                      );
                      return;
                    }
                    setConfirmDialog({
                      open: true,
                      title: "Confirm Password Reset",
                      message: (
                        <>
                          <Box component="span" sx={{ color: "#d32f2f" }}>
                            Are you sure you want to reset the password for
                          </Box>
                          {" "}
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {params.row.username}
                          </Box>
                          {"? A new password will be generated and emailed to them."}
                        </>
                      ),
                      confirmColor: "primary",
                      onConfirm: () => {
                        updateUserPassword.mutate(params.row);
                      },
                    });
                  }}
                  size="small"
                  disabled={isTenantBlocked || isEditing}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    height: "24px",
                    fontSize: "12px",
                    textTransform: "none",
                  }}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, height: 16, my: "auto", display: { xs: "none", md: "block" } }}
            />
          </>
        )}

        <Box
          id="tour-user-actions-cell"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* Edit Action */}
          {!params.row.isNew && canUpdateUser && helpers?.handleEditClick && (
            <>
              <Tooltip
                title={
                  isTenantBlocked
                    ? tenantMessage
                    : isEditing
                      ? "Complete current operation first"
                      : "Edit User"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={isTenantBlocked || isEditing}
                    onClick={(e) => {
                      e.stopPropagation();
                      helpers.handleEditClick();
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <EditIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 0.5, height: 16, my: "auto", display: { xs: "none", md: "block" } }}
              />
            </>
          )}

          {/* Delete Action (Custom implementation for Confirmation) */}
          {!params.row.isNew && canDeleteUser && (
            <Tooltip
              title={
                isTenantBlocked
                  ? tenantMessage
                  : params.row.isTenantAdmin
                    ? "Tenant Admin cannot be deleted"
                    : !currentUser?.isTenantAdmin && params.row.isTenantAdmin
                      ? "Only Tenant Admin can manage this user"
                      : "Delete User"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={
                    isTenantBlocked ||
                    params.row.isTenantAdmin ||
                    isEditing ||
                    (!currentUser?.isTenantAdmin && params.row.isTenantAdmin)
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDialog({
                      open: true,
                      title: "Confirm Deletion",
                      message: `Are you sure you want to delete user ${params.row.username}? This action cannot be undone.`,
                      onConfirm: () => {
                        deleteUserMutation.mutate(params.row.userID);
                      },
                    });
                  }}
                  sx={{ p: 0.5 }}
                >
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, height: 16, my: "auto", display: { xs: "none", md: "block" } }}
          />

          {/* Active Toggle */}
          <Tooltip
            title={
              isTenantBlocked
                ? tenantMessage
                : isEditingAccessList
                  ? "Complete access list changes first"
                  : params.row.isActive
                    ? "Deactivate User"
                    : "Activate User"
            }
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Switch
                onChange={(e) => {
                  const checked = e.target.checked;
                  handleSwitchChange(params, "isActive", e);
                  params.api.updateRows([
                    {
                      id: params.id,
                      isActive: checked ? 1 : 0,
                    },
                  ]);
                }}
                checked={!!params.row.isActive}
                size="small"
                disabled={isTenantBlocked || isEditingAccessList}
                sx={{ transform: "scale(0.8)", mr: -1 }}
                inputProps={{ tabIndex: 0 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    const newChecked = !params.row.isActive;
                    handleSwitchChange(params, "isActive", {
                      target: { checked: newChecked },
                    });
                    params.api.updateRows([
                      {
                        id: params.id,
                        isActive: newChecked ? 1 : 0,
                      },
                    ]);
                  }
                }}
              />
            </Box>
          </Tooltip>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, height: 16, my: "auto", display: { xs: "none", md: "block" } }}
          />

          {/* Role Icon (Tenant Admin OR Company Admin) */}
          <Tooltip
            title={
              isTenantBlocked
                ? tenantMessage
                : isGlobalSuperAdmin
                  ? "Super Admin cannot manage company roles"
                  : params.row.isTenantAdmin
                    ? "Tenant Administrator (Permanent)"
                    : !isCurrentUserSysAdmin
                      ? params.row.issysadm
                        ? "Company Administrator"
                        : "No administrative role"
                      : params.row.issysadm === 1 &&
                          params.row.userID === parseInt(currentUserId)
                        ? "You are the Company Admin. To transfer this role, click the shield icon on another user."
                        : params.row.issysadm
                          ? "Company Administrator (Click to remove/transfer)"
                          : "Make this user Company Admin"
            }
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleAdminIconClick(params);
              }}
              disabled={isTenantBlocked || isGlobalSuperAdmin}
              sx={{ p: 0.5 }}
            >
              <ShieldIcon
                sx={{
                  fontSize: 20,
                  color: params.row.isTenantAdmin
                    ? "#7b1fa2"
                    : params.row.issysadm
                      ? "#1976d2"
                      : isCurrentUserSysAdmin
                        ? "#999"
                        : "#eee",
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const handleCopyPermissions = async (fromUserId, targetParams) => {
    try {
      setCopyPermissionUserId(fromUserId);
      const targetCompanyId = currentCompany || defaultcompany;
      const { data } = await getWithQueryApiServices(
        `${apiRoutes.GET_USER_DETAILS_BY_ID}/${fromUserId}/permissions`,
        `selectedCompany=${targetCompanyId}${tenantString}`,
      );

      if (data) {
        setOuterValue({
          accessList: [
            {
              companyID: parseInt(targetCompanyId),
              categoryList: data.categoryList || [],
              levelList: data.levelList || [],
            },
          ],
          id: targetParams.row.id,
          isNew: targetParams.row.isNew,
        });
      }
    } catch (error) {
      errorToast("Failed to copy permissions");
    }
  };

  const renderEditActions = (params) => {
    // Only show the copy permissions dropdown when adding a new user, not when updating/editing
    if (!params?.row?.isNew) {
      return null;
    }

    return (
      <CopyPermissionsSelect
        params={params}
        copyUsersList={copyUsersList}
        copyPermissionUserId={copyPermissionUserId}
        handleCopyPermissions={handleCopyPermissions}
        isTenantBlocked={isTenantBlocked}
      />
    );
  };

  useEffect(() => {
    if (selectedUser) {
      getWithQueryApiServices(
        `${apiRoutes.GET_USER_DETAILS_BY_ID}/${selectedUser}`,
        `selectedCompany=${defaultcompany}${tenantString}`,
      )
        .then(({ data }) => {
          setUserDetails(data);
          commitSavedAccessList(data?.accessList);
          setCopySessionSource(permissionCompany || currentCompany || null);
          setAddnewUser(true);
        })
        .catch(() => {});
    }
  }, [selectedUser, defaultcompany, tenantString]);

  const handleUserSelection = (item) => {
    if (isEditing) {
      errorToast("Please complete or cancel the current operation first.");
      return;
    }

    setHasPermissionsChanged(false);
    hasUnsavedCopyRef.current = false;
    setCopySessionSource(null);

    if (item?.isNew) {
      const nextDetails = { ...userFormInitVal, ...item };
      setSelectedUser("");
      setUserDetails(nextDetails);
      commitSavedAccessList(nextDetails?.accessList);
      setCopySessionSource(permissionCompany || currentCompany || null);
      setAddnewUser(true);
    } else {
      setSelectedUser(item?.userID);
    }
  };

  const createNewUser = (user) => {
    return { ...user, ...userFormInitVal };
  };

  const updateUser = (user) => {
    return { ...userDetails, ...user };
  };

  const updateUserPassword = useMutation({
    mutationKey: ["updateuserPassword"],
    mutationFn: (user) =>
      putWithQueryApiServices(
        apiRoutes.PASSWORD_RESET,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        user,
      ),
    onSuccess: () => {
      userListRefetch();
      setAddnewUser(false);
      setSelectedUser("");
      setUserDetails(userFormInitVal);
      setConfirmDialog({
        open: false,
        title: "",
        message: "",
        onConfirm: null,
        loading: false,
      });
      successToast("Mail sent with updated password");
    },
    onError: (error) => {
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(error.response?.data?.message || "Failed to reset password");
      }
    },
  });

  const createUserMutation = useMutation({
    mutationKey: ["createUser"],
    mutationFn: (user) =>
      postWithQueryApiServices(
        apiRoutes.CREATE_USER,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        user,
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["usersList"]);
      queryClient.invalidateQueries(["copyUsersList"]);
      userListRefetch();
      setCopyPermissionUserId(""); // Reset copy permission selection
      setOuterValue(null);
      const hasCopyPermission =
        variables?.accessList && variables.accessList.length > 0;
      if (hasCopyPermission) {
        successToast(data?.data?.message || "User Created Successfully");
        if (variables?.title) {
          setPendingSelectUserEmail(variables.title);
        }
      } else {
        successToast("User Created Successfully");
      }
      setIsEditingGrid(false);
    },
    onError: (error) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        const errorMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === "string" ? error?.response?.data : "") ||
          "Failed to create user";
        errorToast(errorMsg);
      }
    },
  });

  const updateUserDetails = useMutation({
    mutationKey: ["updateuserDetails"],
    mutationFn: (user) =>
      putWithQueryApiServices(
        apiRoutes.UPDATE_USER_BY_ID,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        user,
      ),
    onSuccess: (data, variables) => {
      // ✅ HANDOVER LOGIC: If we just demoted ourselves (handover) or backend asks for logout, force logout
      if (
        data?.data?.logout ||
        (Number(variables.userID) === Number(currentUserId) &&
          variables.issysadm === 0)
      ) {
        successToast(
          "Administrative role transferred. Logging out to refresh permissions...",
        );

        // Clear all cookies (except tenantName)
        const allCookies = cookies.getAll();
        Object.keys(allCookies).forEach((cookieName) => {
          if (cookieName !== "tenantName") {
            cookies.remove(cookieName, { path: "/" });
          }
        });

        // Redirect to login
        setTimeout(() => {
          navigate(ROUTE_PATHS.LOGIN || "/");
        }, 1500);
        return;
      }

      queryClient.invalidateQueries(["usersList"]);
      queryClient.invalidateQueries(["copyUsersList"]);
      userListRefetch();

      // ✅ SUCCESS MESSAGE FOR PENDING APPROVAL
      if (data?.data?.pendingApproval) {
        successToast(data.data.message);
      } else {
        successToast("User Updated Successfully");
      }

      if (variables && Number(variables.userID) === Number(selectedUser)) {
        setUserDetails((prev) => ({ ...prev, ...variables }));
      }
      setIsEditingGrid(false);
    },
    onError: (error) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        const errorMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === "string" ? error?.response?.data : "") ||
          "Failed to update user";
        errorToast(errorMsg);
      }
    },
  });

  // ✅ HANDLER FOR DATA GRID SAVES
  const handleRowUpdate = async (newRow) => {
    const formattedRow = { ...newRow };
    if (formattedRow.securityprofileID === "anywhere") {
      formattedRow.allowAnywhere = 1;
      formattedRow.securityprofileID = null;
    } else if (
      !formattedRow.securityprofileID ||
      formattedRow.securityprofileID === ""
    ) {
      formattedRow.allowAnywhere = 0;
      formattedRow.securityprofileID = null;
    } else {
      formattedRow.allowAnywhere = 0;
      formattedRow.securityprofileID = Number(formattedRow.securityprofileID);
    }

    // Determine if this is a new user (creation) or existing user (update)
    // New rows from FullFeaturedCrudGrid have an "isNew" flag or a purely client-side ID
    if (formattedRow.isNew || !formattedRow.userID) {
      const { id, isNew, ...userData } = formattedRow;
      if (outerValue && outerValue.accessList) {
        userData.accessList = outerValue.accessList;
      }
      await createUserMutation.mutateAsync(userData);
    } else {
      await updateUserDetails.mutateAsync(formattedRow);
    }
    return formattedRow;
  };

  const deleteUserMutation = useMutation({
    mutationKey: ["deleteUser"],
    mutationFn: (userId) =>
      deletewithQueryApiServices(
        apiRoutes.DELETE_USER,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        userId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["usersList"]);
      queryClient.invalidateQueries(["copyUsersList"]);
      userListRefetch();
      setSelectedUser("");
      setUserDetails(userFormInitVal);
      setConfirmDialog({
        open: false,
        title: "",
        message: "",
        onConfirm: null,
        loading: false,
      });
      successToast("User deleted successfully.");
    },
    onError: (error) => {
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
      errorToast(error.response?.data?.message || "Failed to delete user");
    },
  });

  // ✅ 7 SIMPLE PERMISSIONS CONFIG
  const PERMISSIONS_LIST = [
    "Edit Categories",
    "Edit Company",
    "Edit Metadata",
    "Edit Users",
    "Reporting",
    "Scan",
    "Search",
  ];

  const isLevelChecked = (companyID, level) => {
    const getCompany = userDetails?.accessList?.find(
      (item) => Number(item?.companyID) === Number(companyID),
    );
    if (!getCompany || !getCompany.levelList) return false;
    const targetResolved = level.toLowerCase().trim();
    return getCompany.levelList.some(
      (l) => resolvePermission(l) === targetResolved,
    );
  };

  const levelOnchange = async (companyID, level, value) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    let workData = [...(userDetails?.accessList || [])].filter(
      (item) => item?.companyID != null,
    );
    const companyIndex = workData.findIndex(
      (item) => Number(item?.companyID) === Number(companyID),
    );
    const targetResolved = level.toLowerCase().trim();
    if (targetResolved === "search" && !value) return;

    if (companyIndex > -1) {
      let getCompany = { ...workData[companyIndex] };
      const currentLevels = getCompany.levelList || [];

      if (value) {
        // Clean out legacy equivalents to keep levels clean and then push new level
        const cleanLevels = currentLevels.filter(
          (item) => resolvePermission(item) !== targetResolved,
        );
        getCompany.levelList = [...cleanLevels, level];
      } else {
        // Filter out new level and any legacy equivalents
        getCompany.levelList = currentLevels.filter(
          (item) => resolvePermission(item) !== targetResolved,
        );
      }
      if (!getCompany.levelList.some((item) => resolvePermission(item) === "search")) {
        getCompany.levelList = [...getCompany.levelList, "Search"];
      }
      workData[companyIndex] = getCompany;
    } else if (value) {
      workData.push({
        companyID,
        levelList: level.toLowerCase() === "search" ? [level] : ["Search", level],
        categoryList: [],
      });
    }

    const newUserDetails = { ...userDetails, accessList: workData };
    setUserDetails(newUserDetails);
    setHasPermissionsChanged(true);

    // Auto-save silently
    try {
      await putWithQueryApiServices(
        apiRoutes.UPDATE_USER_BY_ID,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        newUserDetails,
      );
      commitSavedAccessList(newUserDetails.accessList);
    } catch (error) {
      setUserDetails(userDetails);
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to update permissions",
        );
      }
    }
  };

  const applyPermissionsToCompany = async (targetCompanyID) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    // Get current company's level list
    const sourceCompanyAccess = userDetails?.accessList?.find(
      (item) => Number(item?.companyID) === Number(permissionCompany),
    );
    const sourceLevels = [...(sourceCompanyAccess?.levelList || [])];
    if (!sourceLevels.some((item) => resolvePermission(item) === "search")) {
      sourceLevels.push("Search");
    }

    let workData = [...(userDetails?.accessList || [])].filter(
      (item) => item?.companyID != null,
    );
    const targetCompanyIndex = workData.findIndex(
      (item) => Number(item?.companyID) === Number(targetCompanyID),
    );

    if (targetCompanyIndex > -1) {
      workData[targetCompanyIndex] = {
        ...workData[targetCompanyIndex],
        levelList: [...sourceLevels],
      };
    } else {
      workData.push({
        companyID: Number(targetCompanyID),
        levelList: [...sourceLevels],
        categoryList: [],
      });
    }

    const newUserDetails = { ...userDetails, accessList: workData };
    setUserDetails(newUserDetails);
    hasUnsavedCopyRef.current = true;
    setCopySessionSource(permissionCompany);
  };

  return (
    <Box
      style={{ backgroundColor: "#7285b3", minHeight: "100%" }}
    >
      <BoxContainer>
        <StyledHeader>User Settings and Permission</StyledHeader>

        {isTenantBlocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {tenantMessage}
          </Alert>
        )}

        {(isUsersLoading || isProfilesLoading) && !isCompanySwitching ? (
          <LoadingScreen message="Loading users..." />
        ) : !isGlobalSuperAdmin && !canReadUser ? (
          <PermissionDenied message="You do not have permission to view user settings and permissions. Please contact your administrator." />
        ) : (
          <>
            {isGlobalSuperAdmin && (
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#666" }}
                >
                  Select Company:
                </Typography>
                <FormControl size="small" sx={{ width: { xs: '100%', sm: 250 }, maxWidth: '100%' }}>
                  <Select
                    value={currentCompany || ""}
                    onChange={handleGlobalCompanyChange}
                    displayEmpty
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      '& .MuiSelect-select': {
                        display: 'block',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        paddingRight: '24px',
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: { xs: 'calc(100vw - 52px)', sm: 250 },
                          maxWidth: '100%',
                        }
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Company
                    </MenuItem>
                    {fullCompanyList?.filter((company) => Number(company.dst) !== 0).map((company) => (
                      <MenuItem
                        key={company.companyID}
                        value={company.companyID}
                        sx={{
                          maxWidth: { xs: 'calc(100vw - 52px)', sm: 250 },
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {isEditing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You have unsaved changes.
              </Alert>
            )}

            {/* ✅ PENDING ROLE CHANGE REQUESTS PANEL */}
            {roleRequests?.data?.length > 0 && isCurrentUserSysAdmin && (
              <Box sx={{ mb: 3 }}>
                <Alert
                  severity="info"
                  icon={<ShieldIcon />}
                  sx={{
                    "& .MuiAlert-message": { width: "100%" },
                    border: "1px solid #4A90A4",
                    bgcolor: "#f0f7f9",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Pending Role Change Requests
                    </Typography>
                    <Chip
                      label={`${roleRequests.data.length} New Request${roleRequests.data.length > 1 ? "s" : ""}`}
                      color="info"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: 1 }}>
                    {roleRequests.data.map((request) => (
                      <Box
                        key={request.requestID}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1.5,
                          mb: 1,
                          bgcolor: "white",
                          borderRadius: 1,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        }}
                      >
                        <Box>
                          <Typography variant="body2">
                            <strong>{request.requesterUsername}</strong>{" "}
                            requested to promote{" "}
                            <strong>{request.targetUsername}</strong> to Company
                            Admin in <strong>{request.companyName}</strong>.
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Requested on:{" "}
                            {new Date(
                              request.createdAt.includes("Z") ||
                                request.createdAt.includes("+")
                                ? request.createdAt
                                : `${request.createdAt.replace(" ", "T")}Z`,
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() =>
                              handleRoleRequest.mutate({
                                requestId: request.requestID,
                                action: "approve",
                              })
                            }
                            disabled={
                              handleRoleRequest.isPending &&
                              handleRoleRequest.variables?.requestId ===
                                request.requestID
                            }
                          >
                            {handleRoleRequest.isPending &&
                            handleRoleRequest.variables?.requestId ===
                              request.requestID &&
                            handleRoleRequest.variables?.action === "approve"
                              ? "Processing..."
                              : "Approve & Logout"}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<CancelOutlinedIcon />}
                            onClick={() =>
                              handleRoleRequest.mutate({
                                requestId: request.requestID,
                                action: "reject",
                              })
                            }
                            disabled={
                              handleRoleRequest.isPending &&
                              handleRoleRequest.variables?.requestId ===
                                request.requestID
                            }
                          >
                            {handleRoleRequest.isPending &&
                            handleRoleRequest.variables?.requestId ===
                              request.requestID &&
                            handleRoleRequest.variables?.action === "reject"
                              ? "Processing..."
                              : "Reject"}
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Alert>
              </Box>
            )}

            {/* Only show Read-Only banner if user is NOT a System Admin AND is missing some permissions */}
            {isUserInCompany &&
              !isCurrentUserSysAdmin &&
              (!canUpdateUser || !canCreateUser || !canDeleteUser) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Read-Only Mode:</strong> You are viewing this page
                  with restricted permissions.
                </Alert>
              )}

            <DisabledOverlay disabled={!isUserInCompany}>
              <ParentContainer>
                <Box sx={{ display: "flex", width: "100%" }}>
                  <SplitContainer
                    style={{
                      width: "100%",
                      height: "auto",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <TabPanel value={0} index={0}>
                      <Box
                        id="tour-user-grid"
                        sx={{
                          height: { xs: "auto", md: "60vh" },
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        <FullFeaturedCrudGrid
                          initialRows={allUsersList?.data?.userList}
                          columns={
                            canUpdateUser
                              ? columns
                              : columns.map((c) => ({ ...c, editable: false }))
                          }
                          label="user"
                          handleSelection={handleUserSelection}
                          search={searchUser}
                          setSearch={setSearchUser}
                          refetch={userListRefetch}
                          addFunc={createNewUser}
                          editFunc={updateUser}
                          rowUpdateFun={handleRowUpdate}
                          initialFormValue={userFormInitVal}
                          outerValue={outerValue}
                          setOuterValue={setOuterValue}
                          isTenantBlocked={isTenantBlocked}
                          tenantMessage={tenantMessage}
                          onEditingStateChange={handleGridEditingStateChange}
                          disabled={isEditingAccessList || !canReadUser}
                          isSaving={createUserMutation.isPending || updateUserDetails.isPending}
                          extraActionsRenderer={renderExtraActions}
                          permission={isGlobalSuperAdmin}
                          canCreate={canCreateUser}
                          canUpdate={canUpdateUser}
                          canDelete={canDeleteUser}
                          delFun={null}
                          renderEditActions={
                            canUpdateUser || isGlobalSuperAdmin
                              ? renderEditActions
                              : undefined
                          }
                          selectedRowId={selectedUser || null}
                          hideFooterPagination={true}
                          rowCount={allUsersList?.data?.count || 0}
                          paginationModel={paginationModel}
                          onPaginationModelChange={setPaginationModel}
                          loading={usersFetching}
                        />
                      </Box>
                    </TabPanel>
                  </SplitContainer>
                </Box>
              </ParentContainer>
            </DisabledOverlay>

            {/* AccessList - COMPLETELY UNCHANGED */}
            {userDetails?.userID && (
              <DisabledOverlay disabled={isEditingGrid || !canUpdateUser}>
                <CompanyContainer
                  id="tour-user-access-list"
                  hasuser="true"
                  style={{
                    cursor: addNewUser && !isEditingGrid ? "auto" : "not-allowed",
                    opacity: isEditingGrid ? 0.6 : 1,
                  }}
                >
                  <AccessList
                    userDetails={userDetails}
                    setUserDetails={setUserDetails}
                    addNewUser={addNewUser && !isEditingGrid}
                    setAddnewUser={setAddnewUser}
                    isTenantBlocked={isTenantBlocked}
                    tenantMessage={tenantMessage}
                    onEditingStateChange={handleAccessListEditingStateChange}
                    disabled={isEditingGrid}
                    hasPermissionsChanged={hasPermissionsChanged}
                    applyPermissionsToCompany={applyPermissionsToCompany}
                    currentCompany={permissionCompany}
                    onSelectPermissionCompany={changePermissionCompany}
                    copySessionSource={copySessionSource}
                    onAccessSaved={commitSavedAccessList}
                    isCurrentUserSysAdmin={isCurrentUserSysAdmin}
                    canUpdateUser={canUpdateUser}
                    canSeeAllCompanies={isGlobalSuperAdmin || isActualTenantAdmin}
                    resolvePermission={resolvePermission}
                    isLevelChecked={isLevelChecked}
                    onLevelChange={levelOnchange}
                    permissionsList={PERMISSIONS_LIST}
                  />
                </CompanyContainer>
              </DisabledOverlay>
            )}
          </>
        )}
      </BoxContainer>

      {/* ✅ UPDATED: Security Info Dialog - Shows User + Company Security */}
      <Dialog
        open={securityInfoDialog.open}
        onClose={handleCloseSecurityInfoDialog}
        aria-labelledby="security-info-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
            borderRadius: "14px",
          },
        }}
      >
        <DialogTitle id="security-info-dialog-title" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography sx={{ fontSize: { xs: "15px", sm: "18px" }, fontWeight: "bold", color: "text.primary" }}>
              Security Settings for {securityInfoDialog.user?.username}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {securityLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : securityInfoDialog.user ? (
            <Box sx={{ mt: 2 }}>
              {/* User Level Security */}
              <Typography variant="subtitle2" color="primary" gutterBottom>
                User Level Security
              </Typography>
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                {securityInfoDialog.user.allowAnywhere ? (
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 1 }}>
                    <SecurityBadge securitytype="anywhere" label="Anywhere" />
                    <Typography variant="body2">
                      User can access from anywhere (overrides company
                      restrictions)
                    </Typography>
                  </Box>
                ) : securityInfoDialog.user.securityprofileID ? (
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 1 }}>
                    <SecurityBadge securitytype="user" label="User Profile" />
                    <Typography variant="body2">
                      Using profile:{" "}
                      <strong>
                        {getSecurityProfileName(
                          securityInfoDialog.user.securityprofileID,
                        )}
                      </strong>
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 1 }}>
                    <SecurityBadge securitytype="none" label="None" />
                    <Typography variant="body2">
                      No user-level security (inherits from company)
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Company Level Security */}
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Company Level Security
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                {(() => {
                  const userCompanies = getUserCompaniesSecurityInfo();

                  if (userCompanies.length > 0) {
                    return userCompanies.map((company, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 18, color: "#666" }} />
                        <Typography variant="body2">
                          <strong>{company.name}</strong>:{" "}
                          {company.securityprofileID ? (
                            <Chip
                              size="small"
                              label={company.profileName}
                              sx={{
                                height: 20,
                                fontSize: 10,
                                backgroundColor: "#fff3e0",
                                color: "#f57c00",
                              }}
                            />
                          ) : (
                            <span style={{ color: "#999" }}>
                              No restrictions
                            </span>
                          )}
                        </Typography>
                      </Box>
                    ));
                  } else {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        No company access configured for this user.
                      </Typography>
                    );
                  }
                })()}
              </Box>

              {/* Effective Security Summary */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 1,
                  border: "1px solid #90caf9",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Effective Security
                </Typography>
                <Typography variant="body2">
                  {securityInfoDialog.user.allowAnywhere ? (
                    <>
                      ✅ User can access from <strong>anywhere</strong> because
                      they have "From Anywhere" permission set.
                    </>
                  ) : securityInfoDialog.user.securityprofileID ? (
                    <>
                      🔒 User is restricted by their{" "}
                      <strong>
                        user-level security profile:{" "}
                        {getSecurityProfileName(
                          securityInfoDialog.user.securityprofileID,
                        )}
                      </strong>
                      . Company restrictions are not applied.
                    </>
                  ) : (
                    (() => {
                      const userCompanies = getUserCompaniesSecurityInfo();
                      const hasCompanyRestrictions = userCompanies.some(
                        (c) => c.securityprofileID,
                      );

                      if (hasCompanyRestrictions) {
                        return (
                          <>
                            🏢 User inherits security from their{" "}
                            <strong>assigned companies</strong>. When accessing
                            each company, that company's security profile
                            applies.
                          </>
                        );
                      } else {
                        return (
                          <>
                            🔓 <strong>No security restrictions</strong>. User
                            can access from anywhere as no user or company
                            restrictions are configured.
                          </>
                        );
                      }
                    })()
                  )}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSecurityInfoDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Role Transfer Confirmation Dialog */}
      <Dialog
        open={transferAdminDialog.open}
        onClose={() =>
          setTransferAdminDialog({
            open: false,
            targetUser: null,
            targetParams: null,
            isRequest: false,
          })
        }
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
            borderRadius: "14px",
          },
        }}
      >
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShieldIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography sx={{ fontSize: { xs: "15px", sm: "18px" }, fontWeight: "bold", color: "text.primary" }}>
              {transferAdminDialog.isRequest
                ? "Request Administrative Promotion"
                : "Transfer Administrative Role"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {transferAdminDialog.isRequest ? (
              <>
                You are requesting to promote{" "}
                <strong>{transferAdminDialog.targetUser?.username}</strong> to
                Company Admin.
                <br />
                <br />
                Since an administrator already exists for this company,{" "}
                <strong>they must approve this request</strong> from their
                dashboard.
              </>
            ) : (
              <>
                Are you sure you want to handover the{" "}
                <strong>Company Admin</strong> role to{" "}
                <strong>{transferAdminDialog.targetUser?.username}</strong>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            pt: 0,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1.5, sm: 0 },
            "& > button, & > span": {
              width: { xs: "100%", sm: "auto" },
              marginLeft: { xs: "0px !important", sm: "8px !important" },
            },
          }}
        >
          <Button
            onClick={() =>
              setTransferAdminDialog({
                open: false,
                targetUser: null,
                targetParams: null,
                isRequest: false,
              })
            }
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmTransferAdmin}
            variant="contained"
            color="error"
            startIcon={<ShieldIcon />}
          >
            {transferAdminDialog.isRequest
              ? "Send Request"
              : "Confirm Handover"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ New Generic Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          !confirmDialog.loading &&
          setConfirmDialog({ ...confirmDialog, open: false })
        }
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "450px",
            m: 2,
            overflowX: "hidden", // Prevent horizontal scroll
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              justifyContent: "center",
              "& .MuiAlert-message": {
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1rem",
                width: "100%",
              },
            }}
            icon={false}
          >
            {confirmDialog.title}
          </Alert>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, overflowX: "hidden" }}>
          <DialogContentText
            sx={{ color: "text.primary", textAlign: "center" }}
          >
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: "center", gap: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            variant="outlined"
            disabled={confirmDialog.loading}
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmDialog((prev) => ({ ...prev, loading: true }));
              confirmDialog.onConfirm();
            }}
            variant="contained"
            color={confirmDialog.confirmColor === "primary" ? "primary" : "error"}
            disabled={confirmDialog.loading}
            startIcon={
              confirmDialog.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            sx={{ px: 3 }}
          >
            {confirmDialog.loading ? "Processing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
